import React from 'react';
import { Box, Heading, IconButton, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { DismissSmall } from '@wix/wix-ui-icons-common';
import type { ExistingSiteStoreReadyTask } from '../existingSiteStoreReadyTasks';
import styles from '../ExistingSiteStoreReadyStep.scss';

interface ExistingSiteStoreReadyTaskCardProps {
  task: ExistingSiteStoreReadyTask;
  isSelected: boolean;
  isMobileDrawerOpen: boolean;
  inlinePanel?: React.ReactNode;
  cardRef?: React.Ref<HTMLElement>;
  onSelect: () => void;
  onCloseMobileDrawer: () => void;
}

export function ExistingSiteStoreReadyTaskCard({
  task,
  isSelected,
  isMobileDrawerOpen,
  inlinePanel,
  cardRef,
  onSelect,
  onCloseMobileDrawer,
}: ExistingSiteStoreReadyTaskCardProps) {
  const { t } = useTranslation();
  const classNames = [styles.existingSiteStoreReadyTask, isSelected ? styles.existingSiteStoreReadyTaskSelected : '']
    .filter(Boolean)
    .join(' ');

  return (
    <article
      ref={cardRef}
      className={classNames}
      onClick={onSelect}
      aria-pressed={isSelected}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <div className={styles.existingSiteStoreReadyTaskNumber} aria-hidden="true">
        <span>{task.number}</span>
      </div>
      <div className={styles.existingSiteStoreReadyTaskBody}>
        <Box minWidth={0} direction="vertical" gap="3px">
          <Heading as="h2" size="small">
            {task.title}
          </Heading>
          <Text size="small" secondary>
            {task.description}
          </Text>
        </Box>
      </div>
      {isMobileDrawerOpen ? (
        <IconButton
          priority="secondary"
          size="medium"
          onClick={(event) => {
            event.stopPropagation();
            onCloseMobileDrawer();
          }}
          aria-label={t('existing-site-store-ready.task-card.close-details-aria', { taskTitle: task.title })}
        >
          <DismissSmall />
        </IconButton>
      ) : (
        <div className={styles.existingSiteStoreReadyTaskActionIcon} aria-hidden="true">
          <span className={styles.existingSiteStoreReadyTaskActionPlus}>+</span>
        </div>
      )}
      {inlinePanel && (
        <div
          className={styles.existingSiteStoreReadyTaskDrawer}
          onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => event.stopPropagation()}
        >
          <Box direction="vertical" gap="SP2" width="100%" minWidth={0} paddingTop="SP2" flexBasis="100%">
            {inlinePanel}
          </Box>
        </div>
      )}
    </article>
  );
}
